import { createStore } from 'vuex'
import {
  CACHEDEVICES,
  SELECTEDDEVICES,
  FAULTBILL,
  MAPSITE,
  USERINFO,
  USERADDRESS,
  POINTDETAILOPEN,
} from '@/utils/constant'
import { isWeixin, isWeixinMiniProgram } from '@/utils/common'
import { getStorage, setStorage, removeStorage, getEntryInfo } from '@/utils/index'
import { UserInfo } from '@/types/user'
import { IClients } from '@/types/client'
import { isXapcTabLet } from '@/utils/app'
import CloseAccount from './close-account'
import InsranceStore from './insurance'
import MalfunctionRepair from './malfunction-repair'
import NameplateReissue from './nameplate-reissue'
import MyCredentials from './my-credentials'
import OldBringsNew from './old-brings-new'
import { LandscapeScreenSafeAreaInfo } from '@/types/app'

export default createStore({
  state: {
    userAddress: getStorage(USERADDRESS) || [],
    client: IClients.web,
    cacheDevices: getStorage(CACHEDEVICES) || [],
    selectedDevices: getStorage(SELECTEDDEVICES, window.sessionStorage) || [],
    faultBill: getStorage(FAULTBILL, window.sessionStorage) || null,
    mapSite: getStorage(MAPSITE) || null,
    userInfo: getStorage(USERINFO, window.sessionStorage) || null,
    appStatusBarHeight: 0, // 状态栏高度，在app时会有告知
    appStatusBarHeightOrigin: 0, // 原始状态栏高度，未经过分辨率计算
    appNavHeight: 44, // 导航栏高度，固定值
    userSite: [],
    // 全保审核申诉参数
    appealAllProtect: {
      appealSelectLog: [], // 选择的日志列表
      imageBase64List: [], // 上传的base64照片
      description: '', // 描述
    },
    isLandscape: false, // 是否横屏
    isSrc4Device: false, // 是否SRC4浏览器
    isTableDevice: false, // 是否平板设备
    safeAreaInfo: {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    }, // 安全区域信息
    requestingUserInfo: false, // 是否正在请求用户信息
    wechatInfo: {}, // 绑定的微信信息
    keepAliveIncludeItem: ['malfunction-repair', 'agricultura-loan'],
    pointDetailOpen: getStorage(POINTDETAILOPEN, window.sessionStorage) || false, // 记录网点详情页面的是否展开状态
    entryInfo: getEntryInfo(),
  },
  getters: {
    // 作用于当页面的顶部是通过fixed固定的时候，顶部需要预留的空间
    pagePaddingTop(state) {
      if (state.client === IClients.miniProgram) return 0
      return state.appNavHeight + state.appStatusBarHeight
    },
    isMiniProgram(state) {
      return state.client === IClients.miniProgram || isWeixinMiniProgram()
    },
    isApp(state) {
      return [IClients.android, IClients.ios].includes(state.client)
    },
    getAppealSelectLog(state) {
      return state.appealAllProtect.appealSelectLog
    },
    getAppealAllProtectImage(state) {
      return state.appealAllProtect.imageBase64List
    },
    getAppealProtectDesciption(state) {
      return state.appealAllProtect.description
    },
    appNavHeight(state) {
      return state.appNavHeight
    },
    userGuid(state) {
      const userInfo: UserInfo = state.userInfo as UserInfo
      return userInfo?.guid || ''
    },
  },
  mutations: {
    setPointDetailOpen(state, bool = false) {
      state.pointDetailOpen = bool
      setStorage(POINTDETAILOPEN, bool, window.sessionStorage)
    },
    setUserAddress(state, addressObj) {
      if (addressObj?.userGuid) {
        const uid = addressObj.userGuid
        const userAddress: any[] = state.userAddress as any[]
        const obj = userAddress.find((it: { userGuid: string }) => it.userGuid === uid)
        if (obj) {
          Object.assign(obj, { addressInfo: addressObj.addressInfo })
        } else {
          userAddress.push(addressObj)
        }
        setStorage(USERADDRESS, userAddress as any)
      }
    },
    setClient(state, client: IClients) {
      state.client = client
    },
    setUserInfo(state, info = null) {
      state.userInfo = info
      if (info) {
        setStorage(USERINFO, info, window.sessionStorage)
      }
    },
    setUserSite(state, point = []) {
      state.userSite = point
      if (!state.mapSite) {
        setStorage(MAPSITE, { zoom: 14, center: point })
      }
    },
    setAppStatusBarHeight(state, h = 0) {
      state.appStatusBarHeight = h
    },
    setAppStatusBarHeightOrigin(state, h = 0) {
      state.appStatusBarHeightOrigin = h
    },
    setMapSite(state, item = null) {
      state.mapSite = item
      item ? setStorage(MAPSITE, item) : removeStorage(MAPSITE)
    },
    setFaultBill(state, item = null) {
      state.faultBill = item
      item
        ? setStorage(FAULTBILL, item, window.sessionStorage)
        : removeStorage(FAULTBILL, window.sessionStorage)
    },
    setSelectedDevices(state, devices = []) {
      state.selectedDevices = devices
      setStorage(SELECTEDDEVICES, devices, window.sessionStorage)
    },
    setCacheDevices(state, deviceObj) {
      if (deviceObj?.userGuid) {
        const uid = deviceObj.userGuid
        const cacheDevices: any[] = state.cacheDevices as any[]
        const obj = cacheDevices.find((it: { userGuid: any }) => it.userGuid === uid)
        if (obj) {
          deviceObj.devices.forEach((it: any) => {
            const item = obj.devices.find((ix: any) => ix.guid === it.guid)
            if (item) {
              Object.assign(item, it)
            } else {
              obj.devices.push(it)
            }
          })
        } else {
          cacheDevices.push(deviceObj)
        }
        setStorage(CACHEDEVICES, cacheDevices as any)
      }
    },
    // 保存申诉里面的架次日志
    setAppealSelectLog(state, logList = []) {
      state.appealAllProtect.appealSelectLog = logList
    },
    // 保存全保审核描述以及上传的照片内容
    setAppealDescAndImage(state, param: { imageBase64List: []; description: string }) {
      state.appealAllProtect.description = param.description
      state.appealAllProtect.imageBase64List = param.imageBase64List || []
    },
    // 保存设备基础信息
    setAppPlatformInfo(
      state,
      param: {
        displayMode: string
        platform: string
        safeAreaInfo: LandscapeScreenSafeAreaInfo
      }
    ) {
      state.safeAreaInfo = {
        ...param.safeAreaInfo,
      }
      state.isLandscape = param.displayMode === 'Landscape'
      const platform = param.platform || ''
      state.isSrc4Device = platform.toLocaleUpperCase().indexOf('SRC') !== -1
      state.isTableDevice = platform.toLocaleUpperCase() === 'TABLET' || isXapcTabLet
    },
    addKeepAliveItem(
      state,
      param: {
        routeName: string
      }
    ) {
      const { routeName } = param
      if (state.keepAliveIncludeItem.indexOf(routeName) !== -1) {
        return
      }
      state.keepAliveIncludeItem = state.keepAliveIncludeItem.concat(routeName)
    },
    removeKeepAliveItem(
      state,
      param: {
        routeName: string
      }
    ) {
      const { routeName } = param
      state.keepAliveIncludeItem = state.keepAliveIncludeItem.filter((item) => item !== routeName)
    },
  },
  actions: {},
  modules: {
    CloseAccount,
    InsranceStore,
    MalfunctionRepair,
    NameplateReissue,
    MyCredentials,
    OldBringsNew,
  },
})
